import './polyfills';
import './index.css';
import React, { useEffect } from 'react';
import { store } from 'store';
import 'services/i18n.service';
import RouteManager from 'navigation/RouteManager';
import { createRoot } from 'react-dom/client';
import HttpsRedirect from 'react-https-redirect';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { isProduction } from 'utils/helpers';
import { registerServiceWorks } from 'utils/registerWorker';
import reportAccessibility from 'utils/reportAccessibility';
import reportWebVitals from './reportWebVitals';
import { ErrorBoundary } from 'components/ErrorBoundary';
import { bootstrapPostHogAnonymous } from 'services/posthog.service';
import { isRouteEmbedded } from 'utils/routes.util';
import EmbeddedRoutes from 'navigation/EmbeddedRoutes';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { THEME_OPTION } from 'enums';
import Cookies from 'js-cookie';
import { updateLanguage } from 'store/reducer/setting/slice';
import { COOKIES, LANGUAGE, LOCAL_STORAGE } from 'constants/general';
import { themeSelector } from 'store/reducer/setting/selectors';
import { ROUTES } from 'constants/routes';
import { VALID_REDIRECT_PATHS } from 'constants/routes';
import { updateLocalStorage, getLocalStorage } from 'utils/helpers/storage';
import { captureJoinCodeFromUrl } from 'utils/team-join.util';
import { updateThemeMode } from 'store/reducer/setting/slice';
import { bindApiRateLimitDispatch } from 'services/axios-config';
import { bindStoreAccess } from 'store/store-access';
import { useOnlineStatus } from 'hooks/useOnlineStatus';
import {
  getMediaQueryMatches,
  subscribeToMediaQuery
} from 'utils/matchMedia.util';

captureJoinCodeFromUrl();

if (isProduction) {
  registerServiceWorks();
  bootstrapPostHogAnonymous();
}

const AppContext = () => {
  const dispatch = useAppDispatch();
  const theme = useAppSelectorShallow(themeSelector);

  useEffect(() => {
    const savedTheme = getLocalStorage(LOCAL_STORAGE.THEME);
    let themeToApply: THEME_OPTION;

    if (
      savedTheme &&
      (savedTheme === THEME_OPTION.LIGHT || savedTheme === THEME_OPTION.DARK)
    ) {
      themeToApply = savedTheme as THEME_OPTION;
      dispatch(updateThemeMode(themeToApply));
    } else {
      const prefersDark = getMediaQueryMatches('(prefers-color-scheme: dark)');
      themeToApply = prefersDark ? THEME_OPTION.DARK : THEME_OPTION.LIGHT;
      dispatch(updateThemeMode(themeToApply));
      updateLocalStorage(LOCAL_STORAGE.THEME, themeToApply);
    }

    if (themeToApply === THEME_OPTION.DARK) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      const savedTheme = getLocalStorage(LOCAL_STORAGE.THEME);
      if (!savedTheme) {
        const newTheme = e.matches ? THEME_OPTION.DARK : THEME_OPTION.LIGHT;
        dispatch(updateThemeMode(newTheme));
        updateLocalStorage(LOCAL_STORAGE.THEME, newTheme);
      }
    };

    return subscribeToMediaQuery(
      '(prefers-color-scheme: dark)',
      handleSystemThemeChange
    );
  }, [dispatch]);

  useEffect(() => {
    if (theme === THEME_OPTION.DARK) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    if (theme) {
      updateLocalStorage(LOCAL_STORAGE.THEME, theme);
    }
  }, [theme]);

  useEffect(() => {
    bindStoreAccess(dispatch, store.getState);
    bindApiRateLimitDispatch(dispatch);
  }, [dispatch]);

  useOnlineStatus();

  useEffect(() => {
    const { pathname: redirectPath, search } = window.location;
    VALID_REDIRECT_PATHS.includes(redirectPath) &&
      updateLocalStorage(
        LOCAL_STORAGE.REDIRECT_PATH,
        redirectPath === ROUTES.TODO ||
          redirectPath === ROUTES.ACCOUNTABILITY_BUDDY_ACCEPT ||
          redirectPath === ROUTES.ACCEPT_PROJECT_INVITE
          ? `${redirectPath}${search}`
          : redirectPath
      );
    const currentLang = Cookies.get(COOKIES.LANGUAGE) || LANGUAGE.GB;
    dispatch(updateLanguage(currentLang));
  }, [dispatch]);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        {isRouteEmbedded ? <EmbeddedRoutes /> : <RouteManager />}
      </BrowserRouter>
    </ErrorBoundary>
  );
};

const container = document.getElementById('root');
if (!container) throw new Error('Root element not found');
const root = createRoot(container);
root.render(
  <HttpsRedirect>
    <Provider store={store}>
      <AppContext />
    </Provider>
  </HttpsRedirect>
);

reportAccessibility(React);
reportWebVitals();
